import React, { useState, useMemo } from "react";
import ToolReportPromo from "./ToolReportPromo";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import VipKingOffer from "./VipKingOffer";
import PortalAuthModal from "./PortalAuthModal";
import {
  Sparkle, Wallet, User, SignOut, Moon, List, X,
  Calculator, ChatCircleDots, MoonStars, Gift, Star, House, Heart, Rocket, Sun, Crown,
  Bookmark, ChartLine, Pulse, Eye, MusicNotes, Users, ShieldWarning, Stack, Calendar, Clock,
  Compass, FilmStrip, Headphones, Scroll, CurrencyInr, Target, Coins, Globe, Megaphone, Receipt, Envelope, MagicWand, ChartLineUp, ShieldCheck,
  MagnifyingGlass, GraduationCap, Lightning, Bell, Palette, EyeSlash, Trash, Phone, Article, Robot, Path, TrendDown, Trophy, Diamond, Plus, FlowerLotus,
} from "@phosphor-icons/react";

export const Header = () => {
  const { user, logout, checkAuth } = useAuth();
  const { currency, setCurrency, formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [vipOfferOpen, setVipOfferOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  // Listen for app-wide "open VIP offer" events (fired after a PDF download, etc.)
  React.useEffect(() => {
    const handler = () => {
      // Don't pop the offer to users who are already VIP / already have an
      // active unlock-all window — the AuthContext keeps user fresh.
      if (user?.is_vip || user?.vip_unlocks_all) return;
      setVipOfferOpen(true);
    };
    window.addEventListener("vip:offer:open", handler);
    return () => window.removeEventListener("vip:offer:open", handler);
  }, [user?.is_vip, user?.vip_unlocks_all]);

  const handleLogin = () => {
    // Open the unified sign-in modal (both Google + Portal). The modal's
    // "Login Via Google" button reuses the same startGoogleSignIn flow the
    // header used to trigger directly.
    setAuthOpen(true);
  };

  return (
    <>
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-5 flex items-center justify-between gap-2">
        {/* Hamburger menu trigger — visible on all screen sizes */}
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors"
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <List size={22} weight="bold" />
        </button>

        <Link to="/" className="flex items-center gap-2 sm:gap-3 group" data-testid="logo-link">
          <Moon size={22} weight="duotone" className="text-gold transition-transform group-hover:rotate-12 sm:hidden" />
          <Moon size={26} weight="duotone" className="text-gold transition-transform group-hover:rotate-12 hidden sm:block" />
          <span className="flex flex-col leading-none">
            <span className="font-serif-display text-xl sm:text-2xl tracking-tight">
              Astro<span className="text-gold">Kuber</span>Chat
            </span>
            {/* Brand tagline — soft, italic, Apple/Nike-style positioned just
                below the wordmark. Hidden on the tightest mobile widths so it
                doesn't crowd the hamburger + cart row. */}
            <span
              className="inline-block mt-0.5 text-[9px] sm:text-[11px] italic text-gold/90 tracking-wide self-start"
              data-testid="brand-tagline"
            >
              Tomorrow looks better.
            </span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 lg:gap-7 label-eyebrow" aria-label="Primary navigation">
          {/* Entity-led desktop nav — mirrors HiAstro's topical strategy.
              Every anchor reinforces "AI Astrology" as the ranked entity. */}
          <Link to="/astrologers" data-testid="nav-ai-astrologer" className="hover:text-gold transition-colors" title="Chat with an AI Astrologer 24×7">AI Astrologer</Link>
          <Link to="/kundali-calculator" data-testid="nav-ai-kundli" className="hover:text-gold transition-colors" title="Free AI Kundli — instant Vedic birth chart">AI Kundli</Link>
          <Link to="/horoscope" data-testid="nav-ai-horoscope" className="hover:text-gold transition-colors" title="Personalised AI Horoscope">AI Horoscope</Link>
          <Link to="/tools" data-testid="nav-ai-tools" className="hover:text-gold transition-colors" title="25+ AI astrology tools — Dasha, Navamsa, Past-Life, Numerology">AI Tools</Link>
          <Link to="/compatibility" data-testid="nav-compatibility" className="hover:text-gold transition-colors" title="AI Marriage &amp; Love Compatibility">Compatibility</Link>
          <Link to="/name-numerology" data-testid="nav-numerology" className="hover:text-gold transition-colors" title="AI Numerology &amp; Name analysis">Numerology</Link>
          <Link to="/blog" data-testid="nav-blog" className="hover:text-gold transition-colors" title="AI Astrology blog &amp; guides">Blog</Link>
          <Link to="/community" data-testid="nav-community" className="hover:text-gold transition-colors flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-gold animate-pulse"></span>Community</Link>
          <Link to="/astrologer/login" data-testid="nav-astrologer-portal" className="hover:text-gold transition-colors">Astrologer Portal</Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Currency toggle — small ₹/$ pair so US/UK visitors can switch
              from auto-detected USD back to INR (or vice versa). Settlement
              currency stays INR via Razorpay regardless. */}
          <div
            className="hidden sm:inline-flex items-center rounded-full border border-amber-300/50 bg-white/60 text-[11px] font-bold tracking-wider overflow-hidden"
            data-testid="currency-toggle"
            role="group"
            aria-label="Currency"
          >
            <button
              type="button"
              onClick={() => setCurrency("INR")}
              className={`px-2.5 py-1.5 transition-colors ${currency === "INR" ? "bg-[#3F2D1B] text-amber-50" : "text-stone-600 hover:text-[#3F2D1B]"}`}
              data-testid="currency-toggle-inr"
              aria-pressed={currency === "INR"}
              title="Show prices in Indian Rupees"
            >
              ₹ INR
            </button>
            <button
              type="button"
              onClick={() => setCurrency("USD")}
              className={`px-2.5 py-1.5 transition-colors ${currency === "USD" ? "bg-[#3F2D1B] text-amber-50" : "text-stone-600 hover:text-[#3F2D1B]"}`}
              data-testid="currency-toggle-usd"
              aria-pressed={currency === "USD"}
              title="Show prices in US Dollars (settled in INR via Razorpay; PayPal supported)"
            >
              $ USD
            </button>
          </div>
          {user ? (
            <>
              {user.streak_days >= 2 && (
                <div
                  className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10"
                  title={`${user.streak_days}-day login streak — keep it going!`}
                  data-testid="streak-pill"
                >
                  <span className="text-base leading-none">🔥</span>
                  <span className="text-sm text-gold font-medium">{user.streak_days}-day</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate("/wallet/topup")}
                className="group relative flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-full bg-white border border-amber-200 shadow-[0_1px_4px_rgba(212,175,55,0.15)] hover:shadow-[0_2px_10px_rgba(212,175,55,0.35)] hover:border-amber-300 transition-all cursor-pointer"
                data-testid="wallet-pill"
                title="Add money to wallet"
                aria-label="Add money to wallet"
              >
                {/* Colored wallet icon — amber gradient tile with a wallet glyph */}
                <span className="flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-amber-300 to-amber-500 shadow-inner">
                  <Wallet size={13} weight="fill" className="text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.15)]" />
                </span>
                <span className="text-sm font-semibold text-slate-800 tabular-nums">
                  {formatPrice(user.wallet_balance || 0)}
                </span>
                {/* Bold black + badge — instant "add money" affordance */}
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white group-hover:bg-black group-hover:scale-105 transition-transform shadow-md">
                  <Plus size={14} weight="bold" />
                </span>
              </button>
              <button onClick={() => navigate("/dashboard")} className="hidden sm:flex items-center gap-2 px-3 py-2 btn-ghost rounded-full text-sm" data-testid="user-menu-btn">
                {user.picture
                  ? <img src={user.picture} alt="me" className="w-7 h-7 rounded-full object-cover" />
                  : <User size={18} weight="duotone" />}
                <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                {user.is_vip && (
                  <span className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#3F2D1B] text-[9px] font-bold tracking-widest uppercase">
                    <Crown size={10} weight="fill" /> VIP
                  </span>
                )}
              </button>
              <button onClick={logout} className="hidden md:flex btn-ghost rounded-full p-2" data-testid="logout-btn" title="Logout">
                <SignOut size={18} />
              </button>
            </>
          ) : (
            <button onClick={handleLogin} className="btn-primary rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2" data-testid="login-btn">
              <Sparkle size={14} weight="fill" /> Sign in
            </button>
          )}
        </div>
      </div>
    </header>

    {/* MOBILE SIDE DRAWER — rendered OUTSIDE <header> so the header's
        glass/backdrop-filter doesn't create a containing block that would
        trap the fixed-positioned drawer inside the header's tiny strip. */}
    <MobileMenu
      open={menuOpen}
      onClose={() => setMenuOpen(false)}
      user={user}
      onLogin={handleLogin}
      onLogout={logout}
      onOpenVipOffer={() => setVipOfferOpen(true)}
    />
    <VipKingOffer
      open={vipOfferOpen}
      onClose={() => setVipOfferOpen(false)}
      onSuccess={() => { checkAuth?.(); setVipOfferOpen(false); navigate("/dashboard"); }}
      user={user}
    />
    <PortalAuthModal
      open={authOpen}
      onClose={() => setAuthOpen(false)}
      onSuccess={() => { setAuthOpen(false); navigate("/astrologers"); }}
    />
    </>
  );
};

export function MobileMenu({ open, onClose, user, onLogin, onLogout, onOpenVipOffer }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [searchQuery, setSearchQuery] = useState("");

  // Close menu whenever the route changes
  React.useEffect(() => {
    onClose?.();
  }, [location.pathname]);

  const sections = useMemo(() => [
    // ----- Astrologer Portal (only if this signed-in user is also a
    // registered human astrologer). Sits at the very top so a working
    // astrologer can flip into their work view in one tap.
    ...(user?.astrologer_link ? [{
      id: "astrologer-portal",
      title: "Astrologer Portal",
      icon: House,
      items: [
        {
          action: "open-astro-portal",
          label: `Open my Astrologer Dashboard`,
          icon: House,
          hint: `Signed in as ${user.astrologer_link.name || user.name} · pick chats, calls, withdrawals`,
          highlight: true,
        },
      ],
    }] : []),

    // ----- For You (personalised) -----
    ...(user ? [{
      id: "for-you",
      title: "For You",
      icon: Sparkle,
      items: [
        { to: "/horoscope", label: "Today's Horoscope", icon: Sun, hint: "Personalised for your rashi & lagna", highlight: true },
        { to: "/astrologers", label: "Browse Astrologers", icon: Star, hint: "Talk to verified AI + human experts — start a chat in seconds", accent: "rose" },
        { to: "/reports", label: "Your Reports", icon: Bookmark, hint: "Saved insights — free re-reading forever", highlight: true },
        { to: "/dashboard", label: "My Dashboard", icon: User, hint: "Your kundali, profile & saved data" },
      ],
    }] : [{
      // Logged-out users still see Browse Astrologers as the top CTA
      id: "for-you",
      title: "Start Here",
      icon: Sparkle,
      items: [
        { to: "/astrologers", label: "Browse Astrologers", icon: Star, hint: "Talk to verified AI + human experts — start a chat in seconds", accent: "rose" },
      ],
    }]),

    // ----- Connect (WITHOUT Personal Zoom — that lives in its own section
    // at the very bottom, see below) -----
    {
      id: "connect",
      title: "Connect & Talk",
      icon: ChatCircleDots,
      items: [
        ...(user ? [{ to: "/chats", label: "My Chats", icon: ChatCircleDots, hint: "Your conversations & history" }] : []),
        { to: "/contact", label: "Have a query? Contact us", icon: Envelope, hint: "Drop a note — we read every message and reply within 24 hours" },
      ],
    },

    // ----- VIP & Promos -----
    ...(user && !user.is_vip ? [{
      id: "vip",
      title: "VIP & Offers",
      icon: Crown,
      items: [{
        action: "open-vip-offer",
        label: "Become a VIP King",
        icon: Crown,
        hint: `${formatPrice(4999)} → ${formatPrice(1500)} wallet + unlock all premium tools for 7 days`,
        highlight: true,
        premium: true,
      }],
    }] : []),

    // ----- Astrology Community Group -----
    {
      id: "community",
      title: "Astrology Community Group",
      icon: Users,
      items: [
        { to: "/community", label: "Astrology Community Group", icon: Users, hint: "Join niche groups — Wealth, Gemstone, Relationships, Vedic Learners. Each has its own feed, members and conversations.", accent: "green" },
      ],
    },

    // ----- AstroKuberChat Store (Government-Certified Vedic Gemstones) -----
    {
      id: "store",
      title: "AstroKuberChat Store",
      icon: Diamond,
      items: [
        { to: "/store", label: "Certified Vedic Gemstones", icon: Diamond, hint: "Hand-picked, government-lab certified natural gemstones — Italian Red Coral, Yellow Sapphire, Ruby & more. Insured shipping · 7-day return.", highlight: true, isNew: true },
      ],
    },

    // ----- Learn Astrology (moved to the bottom of the menu — see below) --

    // ----- Premium Tools (paid / viral / flagship) -----
    {
      id: "premium",
      title: "Premium Tools",
      icon: Sparkle,
      items: [
        { to: "/twenty-year-prediction", label: "20-Year Vedic Forecast · Premium Report", icon: Scroll, hint: "₹899 one-time · 40+ page personalised PDF — birth-day Vedic profile, gems, rudraksha, sade-sati, doshas, numerology, 9-planet readings + year-by-year 20-year arc", highlight: true, premium: true, isNew: true },
        { to: "/astro-visualization", label: "Astro Visualization · Life Journey by Mahadasha", icon: Path, hint: "World's-first Astro-Visual · cinematic photorealistic road-journey through your mahadashas, palette-morph per phase, key events + placement-driven readings · ₹5,000 lifetime per chart", highlight: true, premium: true, isNew: true },
        { to: "/life-replay", label: "Life Replay · Your Karmic Timeline", icon: FilmStrip, hint: "Cinematic chapter-by-chapter life timeline · 9 dasha chapters, Saturn/Jupiter returns, personalised narratives", highlight: true, isNew: true },
        { to: "/astro-gps", label: "Astro GPS · Best Cities For Your Destiny", icon: Compass, hint: "Astrocartography + Vedic Dig Bala · 167 cities ranked for money / love / fame / peace / spirituality", highlight: true, isNew: true },
        { to: "/celebrities", label: "AstroKuberChat Celebrity Data · 1,500+ verified charts", icon: Crown, hint: "1,500+ verified birth charts across billionaires, rags-to-riches icons, monarchs, lottery winners and spiritual gurus · D1 + D9 + the exact Dasha they rose / awakened under · ₹15,000 lifetime", highlight: true, isNew: true },
        { to: "/tools/celebrity-pattern-recognizer", label: "Celebrity Placements Pattern Recognizer", icon: Target, hint: "Ask a question like 'Where was Rahu in the Varshaphal when they won the lottery?' and see the statistical pattern across a whole celebrity category · ₹3,000 / 30 days", highlight: true, premium: true, isNew: true },
        { to: "/tools/celebrity-lookalike", label: "Celebrity Lookalike Calculator", icon: Star, hint: "Pick a lagna + any planet-house-sign combo and this tool scans our whole celebrity catalog for exact chart matches.", isNew: true },
        { to: "/varshaphal", label: "Varshaphal · Yearly Reading", icon: Sparkle, hint: "Tajaka solar-return: muntha, year-lord, sahams, sudden gains & job-loss flags", highlight: true, isNew: true },
        { to: "/dasha", label: "Vimshottari Dasha · Deep Read", icon: Pulse, hint: "Full MD→AD→PD breakdown — D1, D9 & D10 lenses · overall / relationship / career", highlight: true, isNew: true },
        { to: "/saptavimshamsha", label: "Saptavimshamsha · Hidden Strength", icon: Eye, hint: "D27 Bhamsha · true personality + wealth potential through 1/2/10/11 + rare yogas", highlight: true, isNew: true },
        { to: "/numerology-deep", label: "Deep Numerology & Lo Shu Grid", icon: Calculator, hint: "Driver, Loshu, lucky mobile/house/vehicle + 12-mo forecast", highlight: true, isNew: true },
        { to: "/planet-effects", label: "9-Planet Effects & Remedies", icon: Star, hint: "All 9 grahas — placement, effect, mantra, gemstone", highlight: true, isNew: true },
        { to: "/bhrigu-bindu", label: "Bhrigu Bindu · Purpose of Life", icon: Sparkle, hint: "Karmic midpoint of Moon+Rahu — your soul mission", highlight: true, isNew: true },
        { to: "/kp-astrology", label: "KP Sub-Lord Console · Krishnamurti Paddhati", icon: Target, hint: "9 planets × 4-tier sub-lord chain · 12 cuspal sub-lords (CSL) · Ruling Planets · 9 mahadashas × 9 antardashas with deterministic KP insights", highlight: true, isNew: true },
        { to: "/past-life", label: "Past Life Reading · D60", icon: Moon, hint: "11-chapter soul biography from your Shashtiamsa chart — who you WERE & what karma activates now", highlight: true, isNew: true },
        { to: "/navamsa", label: "D9 Navamsa · Marital Karma", icon: Heart, hint: "Spouse profile, 7H, vargottama, marriage remedies", highlight: true, isNew: true },
        { to: "/rudramsha", label: "Rudramsha · D11 Wealth Calculator", icon: Coins, hint: "D11 chart — wealth, gains, financial karma & sudden-money windows", highlight: true, isNew: true },
        { to: "/wealth-hora", label: "D2 Hora · Wealth Calculator (holistic)", icon: Coins, hint: "AstroKuber's full 12-house D2 wealth chart — every planet dispersed across all signs (not restricted to Cancer/Leo). Chart + house-by-house analysis + remedies + practical business & savings suggestions + Celebrity DB research. Pure math, no AI.", highlight: true, premium: true, isNew: true },
        { to: "/doshas", label: "Doshas & Remedies · D1 + D9", icon: ShieldWarning, hint: "Mangal, Pitru, Shrapit, Kaal-Sarpa, Grahan, Gandanta + remedy playbook", highlight: true },
        { to: "/raja-yogas", label: "Raja Yogas · Vedic + Jaimini", icon: Crown, hint: "Gajakesari, Panchamahapurusha, Neech Bhanga, Kendra-Trikona + chara karakas (AK..DK) & Karakamsa", highlight: true, isNew: true },
        { to: "/divisional-charts", label: "Divisional Charts · D1 → D60", icon: Stack, hint: "All 19 vargas in one calculator — pick D1..D60, see sign, house, nakshatra, deity, dignity per planet", highlight: true, isNew: true },
        { to: "/tools/baby-muhurat", label: "Baby Delivery Muhurat", icon: Calendar, hint: "Best date & time for a planned delivery: classical birth muhurat + lagna strength + mother's Chandrashtama", highlight: true, premium: true, isNew: true },
        { to: "/career-remedy", label: "Career Remedy Nakshatra Secret", icon: Target, hint: "Your 10th lord, its nakshatra and a secret remedy, one of the most powerful ways to activate your career · ₹1,500", highlight: true, premium: true, isNew: true },
        { to: "/bhagya-remedy", label: "Bhagya Unlock Remedy", icon: Sparkle, hint: "Career or fortune stuck? Find the Pratyadhi Devata of the 9th lord from your Moon and the mantra that removes the block · ₹500", highlight: true, premium: true, isNew: true },
        { to: "/d10-remedy", label: "D10 Career Activation Remedy", icon: Target, hint: "Sudden opposition at work? The D10 deity of your Mahadasha and Antardasha lords (Indra, Agni, Varuna, Kubera…) and its mantra · ₹500", highlight: true, premium: true, isNew: true },
        { to: "/muhurat", label: "Muhurat Finder · Best dates & times", icon: Calendar, hint: "Pick activity (health, finance, home, gold, family, job, launch) → best days + best windows + days to avoid", highlight: true, isNew: true },
        { to: "/marriage-muhurat", label: "Marriage Muhurat Calculator", icon: Heart, hint: "Enter BOTH partners' birth details, pick a year (2026-2030), get every classical-Vedic auspicious wedding date — chandrashtama-clear, Guru/Shukra clear, non-Rikta tithi, best 4-hour muhurat window. 100% free · pure math · no AI", highlight: true, isNew: true },
        { to: "/ishta-devata", label: "Ishta Devata · Main deity to worship", icon: Crown, hint: "Jaimini: Atmakaraka in D9 (Karakamsa) → 12th from it reveals your moksha-deity", highlight: true, isNew: true },
        { to: "/ashtakavarga", label: "Ashtakavarga · Bindu strength of every house", icon: ChartLine, hint: "BAV + SAV bindus per house & sign · wealth-alignment & lucky-sign relationship insights", highlight: true, isNew: true },
        { to: "/rashi-tulya-navamsa", label: "Rashi Tulya Navamsa · Superimposed chart", icon: Stack, hint: "D9 planets overlaid on D1 → which extra house themes each planet's dasha will activate", highlight: true, isNew: true },
        { to: "/kalachakra-dasha", label: "Kalachakra Dasha · Major life turnarounds", icon: Clock, hint: "Jaimini wheel-of-time · 9 MDs × 9 ADs · marriage / wealth / promotion / dusthana / turnaround windows tagged with timing", highlight: true, isNew: true },
        { to: "/chakras", label: "Chakra & Purushartha Heat-Map", icon: Sparkle, hint: "Which chakra & life-pursuit your chart fires", highlight: true, isNew: true },
        { to: "/wealth-programming", label: "Wealth Programming · Free Subliminals", icon: Headphones, hint: "Hand-picked YouTube subliminals + Lakshmi & Kubera mantras to rewire your money story · 100% free", highlight: true, isNew: true },
      ],
    },

    // ----- Free Tools -----
    {
      id: "free-tools",
      title: "Free Tools",
      icon: Calculator,
      items: [
        { to: "/kundali-calculator", label: "Free Kundali Calculator", icon: Calculator, hint: "Swiss Ephemeris-grade chart", highlight: true },
        { to: "/compatibility", label: "Compatibility · Guna Milan", icon: Heart, hint: "Ashta-koota 36-point match", highlight: true },
        { to: "/nakshatra", label: "Nakshatra Deep Analysis", icon: MoonStars, hint: "Janma nakshatra, yoni, gana, bird & more", highlight: true },
        { to: "/tools/pushkar", label: "Pushkar Navamsa & Bhaga · Free", icon: FlowerLotus, hint: "Which of your 9 planets sit in Pushkar Navamsa arcs (auspicious 3°20' zones) or ±1° of the exact Pushkar Bhaga degree — the classical Vedic map of hyper-benefic placements.", highlight: true, isNew: true },
        { to: "/name-numerology", label: "Name Numerology Calculator", icon: Calculator, hint: "Is your name lucky for your birth date?", highlight: true },
        { to: "/rahu-placement", label: "Rahu Placement Calculator", icon: Rocket, hint: "Your house-by-house wealth playbook", highlight: true },
        { to: "/tools", label: "All Tools · Index", icon: Stack, hint: "Browse the full catalogue in one place" },
      ],
    },

    // ----- Daily insights & account -----
    {
      id: "account",
      title: "Daily Brief & Account",
      icon: Wallet,
      items: [
        { to: "/#brief", label: "Today's Panchang & Brief", icon: MoonStars, hint: "Tithi, nakshatra, transits, eclipses", event: "panchang:open" },
        ...(user ? [
          { to: "/dashboard#wallet", label: "Wallet & Order History", icon: Wallet, hint: "Top-ups, invoices, GST receipts" },
        ] : []),
        // Hide the "for onboarded astrologers" login link when this user is
        // already linked to an astrologer account — they'll see the dedicated
        // "Astrologer Portal" section at the top instead.
        ...(user?.astrologer_link ? [] : [
          { to: "/astrologer/login", label: "Astrologer Portal", icon: House, hint: "For onboarded astrologers" },
        ]),
      ],
    },

    // ----- Super Admin -----
    ...(user && user.is_super_admin ? [{
      id: "super-admin",
      title: "Super Admin",
      icon: ShieldCheck,
      items: [
        { to: "/admin/revenue", label: "Revenue · Super Admin", icon: CurrencyInr, hint: "Day · week · month revenue with auto-insights across every paying stream", highlight: true, premium: true },
        { to: "/admin/analytics", label: "Analytics · Super Admin", icon: ChartLine, hint: "Live app stats — users, chats, revenue, top astrologers", highlight: true, premium: true },
        { to: "/admin/demographics", label: "Demographics · Super Admin", icon: Globe, hint: "Country-level user breakdown — today · 7d · 30d · YTD · all-time, with 30-day signup timeline", highlight: true, premium: true },
        { to: "/admin/ads-performance", label: "Ad Performance · Super Admin", icon: Megaphone, hint: "Google Ads funnel — clicks → signups → first chat → first paid topup, with per-country & per-campaign breakdown", highlight: true, premium: true },
        { to: "/admin/recent-purchases", label: "Recent Purchases · Super Admin", icon: Receipt, hint: "Most-recent paid reports — tool unlocks · 20-Year Forecast · VIP King — with buyer, product, amount and date. Top 10 / 20 / 30 / 50", highlight: true, premium: true },
        { to: "/admin/store-orders", label: "Store Orders · Gemstones · Super Admin", icon: Diamond, hint: "Every gemstone checkout — buyer name, shipping address, phone, email, product SKU, price and Razorpay payment id. Copy-to-clipboard on every field for quick courier dispatch.", highlight: true, premium: true, isNew: true },
        { to: "/admin/recent-topups", label: "Recent Top-ups · Super Admin", icon: Wallet, hint: "Most-recent wallet recharges across Razorpay (INR) and PayPal (USD) with GST, pack-bonus and coupon-bonus math. Top 10 / 20 / 30 / 50", highlight: true, premium: true },
        { to: "/admin/contact-queries", label: "Contact Queries · Super Admin", icon: Envelope, hint: "Visitor messages from the Contact Us form — reply, mark read or replied", highlight: true, premium: true },
        { to: "/admin/global-markets", label: "Global Markets · Super Admin", icon: ChartLineUp, hint: "Vedic macro forecaster — Jupiter / Saturn / Rahu transits scored against 45 historical US + India events from 1929 to 2026", highlight: true, premium: true },
        { to: "/admin/lottery-indicator", label: "Lottery Indicator · Super Admin", icon: Coins, hint: "KP 2/3/5/6/8/11 combo × Vimshottari × Kalachakra × Varshaphal — find sureshot windfall windows for any chart", highlight: true, premium: true },
        { to: "/admin/lottery-dates", label: "Lottery Purchase Date Calculator · Super Admin", icon: Coins, hint: "Best dates and exact times to buy a lottery ticket: the transit Moon in Purvashadha (the greatest), Dhanishta, Mula, Uttarabhadrapada and others, boosted by a Venus link", highlight: true, premium: true, isNew: true },
        { to: "/admin/karma-vipaka", label: "Karma Vipaka Samhita · Super Admin", icon: Scroll, hint: "Ascendant + 9 planets by nakshatra pada — tap any one to read its past-life story and expiation from the Karma Vipaka Samhita", highlight: true, premium: true, isNew: true },
        { to: "/admin/customer-lookup", label: "Customer Payment Lookup · Super Admin", icon: Wallet, hint: "Look up a customer by email — see their last 10 transactions, wallet balance & flag stuck/failed payments", highlight: true, premium: true },
        { to: "/admin/current-vips", label: "Current VIPs · Super Admin", icon: Crown, hint: "Everyone with an active VIP King pass — when they subscribed, when it expires, VIP credit left and exactly which tools they have unlocked right now", highlight: true, premium: true, isNew: true },
        { to: "/admin/wallet-deductions", label: "Wallet Deductions Audit · Super Admin", icon: TrendDown, hint: "Every rupee that LEFT a customer's wallet — chat billing + tool unlocks, unified chronological ledger. Kill false 'my balance disappeared' claims with proof.", highlight: true, premium: true },
        { to: "/admin/llm-usage", label: "Universal Key Spend · Super Admin", icon: Sparkle, hint: "Real-time dashboard of every LLM call the app makes — text, image, video. See exactly where the Emergent Universal Key is being burned, by feature and by model.", highlight: true, premium: true },
        { to: "/admin/whales", label: "Whales Finder · Super Admin", icon: Trophy, hint: "Top-paying customers across every revenue source (top-ups + Razorpay tool unlocks + 20-year reports + Personal Zoom bookings). Know who to nurture.", highlight: true, premium: true },
        { to: "/admin/celebrity-cache", label: "Celebrity DB Cache · Super Admin", icon: Star, hint: "Pre-compute the deterministic kundali analyses, Varshaphals and OG images for every celebrity so page-loads are instant.", highlight: true, premium: true },
        { to: "/admin/add-celebrity", label: "Add Celebrity from AstroDatabank · Super Admin", icon: Star, hint: "Paste an astro.com AstroDatabank URL and this tool auto-parses the birth data (regex + timezonefinder, zero LLM spend) into your celebrity catalog.", highlight: true, premium: true },
        { to: "/admin/delete-celebrity", label: "Delete Celebrities · Super Admin", icon: Star, hint: "Pick a category, multi-select any custom-added celebrities and bulk-delete them. Search bar filters the list. Hardcoded catalog rows are read-only.", highlight: true, premium: true },
        { to: "/admin/active-online", label: "Active Online · Super Admin", icon: Users, hint: "Realtime — who is on the site right now", highlight: true, premium: true },
        { to: "/admin/recent-ratings", label: "Recent Ratings · Super Admin", icon: Star, hint: "Last 10 chat ratings with astrologer + customer + comment", highlight: true, premium: true },
        { to: "/admin/seed-reviews", label: "Seed Reviews · Super Admin", icon: MagicWand, hint: "Bulk-add 5-star reviews to any astrologer — random names, curated blurbs, organic-looking dates", highlight: true, premium: true },
        { to: "/admin/raga-remedies", label: "Raga Remedies · Super Admin", icon: MusicNotes, hint: "Classical Hindustani raga prescriptions matched to natal placements", highlight: true, premium: true },
        { to: "/admin/astrologer-applications", label: "Astrologer Applications", icon: ShieldWarning, hint: "Review people who applied to become astrologers", highlight: true },
        { to: "/admin/astrologers", label: "Delete Astrologers · Super Admin", icon: ShieldWarning, hint: "Hard-delete any astrologer account and scrub their chats / reviews", highlight: true, premium: true },
        { to: "/admin/forum", label: "Community · Forum Moderation", icon: ShieldCheck, hint: "Reports queue, ban users, pin posts, forum analytics", premium: true },
        { to: "/admin/library", label: "Astrology Library · Super Admin", icon: Stack, hint: "Private PDF research library — upload, organise by author/topic, full-text search, in-browser reader + download", highlight: true, premium: true },
        { to: "/admin/nadi-astrology", label: "Nadi Astrology · Super Admin", icon: Scroll, hint: "Deep Nadi profile engine — 50+ Bhrigu/Nandi yogas detected and synthesised into 11 deep-dive life sections (Personality, Health, Wealth, Relationships, Career, Travel, Job-Gain, Job-Loss, Lottery, Children, Spirituality)", highlight: true, premium: true },
        { to: "/admin/users", label: "User Database · Super Admin", icon: Users, hint: "Every signed-up user with name, email, country and signup date — bulk-select and send a custom marketing/announcement email broadcast in one click via Resend", highlight: true, premium: true },
        { to: "/admin/grant-unlock", label: "Grant a Free Report · Super Admin", icon: Gift, hint: "Gift one paywalled tool unlock (Rudramsha, Rahu, Numerology Deep, etc.) to any signed-up user by email — useful for CS goodwill, influencer comps, prize fulfilment", highlight: true, premium: true },
        { to: "/admin/astrologer-visibility", label: "Astrologer Visibility · Super Admin", icon: EyeSlash, hint: "Two kill-switches that hide ALL AI astrologers or ALL human astrologers from the public /astrologers page. Useful for a 'human-only weekend', pausing AI for moderation, or covering staffing gaps.", highlight: true, premium: true },
        { to: "/admin/promos", label: "In-Chat Promos · Super Admin", icon: Gift, hint: "Toggle the marketing ribbons inside every seeker chat. Right now controls the Anniversary 50% Cashback banner — flip OFF to make chats look normal again with no promo CTA.", highlight: true, premium: true },
        { to: "/admin/account-delete", label: "Delete Account · Super Admin", icon: Trash, hint: "Search a signed-up customer by email and permanently wipe their account along with every saved chat, message, wallet order, report and other user-scoped data. Two-step confirm; super-admin and human-astrologer accounts are blocked.", highlight: true, premium: true },
        { to: "/admin/voice-calls", label: "Recent Voice Calls · Super Admin", icon: Phone, hint: "Last 25 PSTN/WebRTC call attempts with Twilio leg-status + ErrorCode + ErrorMessage. Use to diagnose 'Sorry, an application error has occurred' audio errors heard by seekers.", highlight: true, premium: true },
        { to: "/admin/push", label: "Push Notifications · Super Admin", icon: Bell, hint: "Broadcast a phone-style push notification (title, body, tap-through URL) to everyone who installed the PWA and enabled alerts — iPhone, Android and Desktop combined", highlight: true, premium: true },
        { to: "/super-admin/pdf-cache", label: "Purge PDF Cache · Super Admin", icon: Trash, hint: "Clear the cached PDF bytes on paid reports (per tool, per customer, or across the board) so the next download regenerates fresh with the latest renderer code. Use after shipping a report-content fix.", highlight: true, premium: true },
        { to: "/admin/themes", label: "Site Theme (US only) · Super Admin", icon: Palette, hint: "Flip the live theme served to US visitors only — Royal Amber (default) or Cosmic Night Sky (vibrant blue with stars + planets). Indian audience is never affected. Reversible at any time.", highlight: true, premium: true },
        { to: "/admin/withdrawals", label: "Astrologer Withdrawals · Super Admin", icon: Wallet, hint: "Pending payout requests from human astrologers — see how much each is owed, mark as paid once you've sent the bank/UPI transfer. Audit trail of every payout.", highlight: true, premium: true },
        { to: "/admin/blog", label: "Blog CMS · Super Admin", icon: Article, hint: "Create, edit, publish or delete blog posts — appears on /blog. Hybrid with rankai.ai; local posts win on slug collision.", highlight: true, premium: true },
        { to: "/admin/autoblog", label: "Auto-Blog · Super Admin", icon: Robot, hint: "Daily AI-written blog posts. Free trending-keyword fetch + Claude Haiku writer + auto-publish at 02:00 UTC. Style locked to the 'Find a Verified Vedic Astrologer' reference piece. Kill-switch and 28-day plan visible.", highlight: true, premium: true },
      ],
    }] : []),

    // ----- Personal Zoom Reading with AstroSachin -----
    // Placed at the very bottom of the menu as its own standalone section so
    // it doesn't compete with the everyday nav items. Product decision
    // 2026-07-28: this is a niche premium 1:1 CTA, not a daily-use link.
    {
      id: "personal-zoom",
      title: "Personal Zoom Reading",
      icon: Crown,
      items: [
        {
          to: "/personal-zoom-meeting",
          label: "Personal Zoom Reading with AstroSachin",
          icon: Crown,
          hint: "60-min private 1:1 with the founder · USD $120",
          highlight: true,
          premium: true,
        },
      ],
    },

    // ----- Learn Astrology (moved from the top to the very bottom on
    // 2026-07-28 — the course is a niche premium CTA, not a daily-use link)
    {
      id: "learn",
      title: "Learn Astrology",
      icon: GraduationCap,
      items: [
        { to: "/course-booking", label: "Astrology Learning Course · Book your Ticket", icon: GraduationCap, hint: "Course by Messenger of Shiva · 10 downloadable videos + 1 doubt-clearing session + lifetime Celebrity DB access (worth ₹15,000 free) · ₹24,000 · delivery by January 2027", highlight: true, premium: true },
      ],
    },
  ], [user, formatPrice]);

  // Filter sections live by search query (matches label + hint, case-insensitive)
  const q = searchQuery.trim().toLowerCase();
  const filteredSections = useMemo(() => {
    if (!q) return sections;
    return sections
      .map((sec) => ({
        ...sec,
        items: sec.items.filter((it) => {
          const lbl = (it.label || "").toLowerCase();
          const hint = (it.hint || "").toLowerCase();
          return lbl.includes(q) || hint.includes(q);
        }),
      }))
      .filter((sec) => sec.items.length > 0);
  }, [sections, q]);

  const totalMatches = filteredSections.reduce((n, s) => n + s.items.length, 0);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        data-testid="mobile-menu-backdrop"
      />
      {/* Drawer */}
      <aside
        className={`fixed top-0 left-0 bottom-0 z-[70] w-[84%] max-w-[380px] bg-[#FAF4E8] shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
        data-testid="mobile-menu-drawer"
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-soft">
          <Link to="/" onClick={onClose} className="flex items-center gap-2">
            <Moon size={22} weight="duotone" className="text-gold" />
            <span className="flex flex-col leading-none">
              <span className="font-serif-display text-xl">Astro<span className="text-gold">Kuber</span>Chat</span>
              <span className="mt-0.5 text-[10px] italic text-gold/90 tracking-wide self-start" data-testid="mobile-menu-tagline">
                Tomorrow looks better.
              </span>
            </span>
          </Link>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5" aria-label="Close menu" data-testid="mobile-menu-close-btn">
            <X size={20} />
          </button>
        </div>

        {/* User summary */}
        {user ? (
          <div className="px-5 py-4 border-b border-soft bg-[#FFF6E0]/40">
            <div className="flex items-center gap-3">
              {user.picture
                ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                : <User size={32} weight="duotone" className="text-gold" />}
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{user.name}</div>
                <div className="text-muted text-xs truncate">{user.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-xs flex-wrap">
              <Link
                to="/wallet/topup"
                className="group inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-white border border-amber-200 shadow-sm hover:border-amber-300 hover:shadow-md transition"
                data-testid="mobile-wallet-pill"
                aria-label="Add money to wallet"
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-md bg-gradient-to-br from-amber-300 to-amber-500 shadow-inner">
                  <Wallet size={11} weight="fill" className="text-white" />
                </span>
                <span className="text-slate-800 font-semibold tabular-nums">{formatPrice(user.wallet_balance || 0)}</span>
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white group-hover:scale-105 transition-transform">
                  <Plus size={11} weight="bold" />
                </span>
              </Link>
              {/* Quick CTA — sits inline with the wallet pill so the upsell is
                  one tap away whenever the menu is open. Hidden for users who
                  already hold an active VIP unlock; for them the gold
                  "VIP King · Active" banner below this row is enough. */}
              {!(user.vip_unlocks_all && user.vip_unlock_days_left > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    onClose?.();
                    setTimeout(() => onOpenVipOffer?.(), 80);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[#3F2D1B] font-semibold border border-amber-400 bg-gradient-to-r from-amber-300 via-amber-200 to-amber-300 hover:from-amber-400 hover:to-amber-400 shadow-sm"
                  data-testid="mobile-become-vip-pill"
                >
                  <Crown size={12} weight="fill" className="text-amber-700" />
                  <span>Become a VIP</span>
                </button>
              )}
              {user.streak_days >= 2 && (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 text-gold">
                  🔥 {user.streak_days}-day
                </span>
              )}
            </div>
            {user.vip_unlocks_all && user.vip_unlock_days_left > 0 && (
              <div
                className="mt-3 flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] shadow-md border border-amber-700/40"
                data-testid="mobile-menu-vip-banner"
              >
                <Crown size={18} weight="fill" className="text-amber-300 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-amber-300 leading-none">
                    VIP King · Active
                  </div>
                  <div className="text-xs mt-0.5 text-amber-50/95 leading-tight">
                    <b>{user.vip_unlock_days_left} day{user.vip_unlock_days_left === 1 ? "" : "s"}</b> left of unlock-all access
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="px-5 py-4 border-b border-soft">
            <button
              onClick={onLogin}
              className="btn-primary rounded-full w-full py-2.5 text-sm font-medium flex items-center justify-center gap-2"
              data-testid="mobile-menu-signin-btn"
            >
              <Sparkle size={14} weight="fill" /> Sign in
            </button>
            <p className="text-muted text-[11px] mt-2 text-center font-light">
              Get a free kundali, daily brief & ₹100 welcome credit.
            </p>
          </div>
        )}

        {/* Search bar */}
        <div className="px-4 py-3 border-b border-soft bg-[#FAF4E8]">
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tools, astrologers, admin…"
              className="w-full pl-9 pr-9 py-2.5 rounded-full border border-soft bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
              data-testid="mobile-menu-search-input"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-black/5 text-muted"
                aria-label="Clear search"
                data-testid="mobile-menu-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {q && (
            <div className="text-[11px] text-muted mt-1.5 pl-1">
              {totalMatches === 0 ? "No matches" : `${totalMatches} result${totalMatches === 1 ? "" : "s"}`}
            </div>
          )}
        </div>

        {/* Menu items — sectioned */}
        <nav
          className="flex-1 overflow-y-auto px-3 py-3"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
          data-testid="mobile-menu-items"
        >
          {filteredSections.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MagnifyingGlass size={28} weight="duotone" className="mx-auto text-muted mb-3" />
              <p className="text-sm text-[#3F2D1B] font-medium">No results for &ldquo;{searchQuery}&rdquo;</p>
              <p className="text-[11px] text-muted mt-1">Try a different keyword or clear the search.</p>
            </div>
          ) : (
            filteredSections.map((section, sIdx) => {
              const SecIcon = section.icon;
              return (
                <div key={section.id} className={sIdx > 0 ? "mt-5" : ""} data-testid={`menu-section-${section.id}`}>
                  {/* Section header (hidden when searching to keep the list compact) */}
                  {!q && (
                    <div className="flex items-center gap-2 px-3 mb-1.5">
                      <SecIcon size={12} weight="fill" className="text-amber-600" />
                      <span className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">{section.title}</span>
                      <span className="flex-1 h-px bg-gradient-to-r from-amber-200/60 to-transparent" />
                    </div>
                  )}
                  <div className="space-y-1">
                    {section.items.map((item, i) => {
                      const Icon = item.icon;
                      const containerClass = `flex items-start gap-3 px-3 py-3 rounded-md transition-all w-full text-left ${
                        item.premium
                          ? "bg-gradient-to-r from-amber-200 via-amber-300 to-amber-400 text-[#3F2D1B] shadow-md hover:shadow-lg hover:scale-[1.01]"
                          : item.accent === "rose"
                          ? "bg-gradient-to-r from-rose-500 via-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-200/60 hover:shadow-xl hover:scale-[1.01]"
                          : item.accent === "green"
                          ? "bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-200/60 hover:shadow-xl hover:scale-[1.01]"
                          : item.highlight
                          ? "bg-[#FFB800]/15 border border-[#FFB800]/40"
                          : "hover:bg-black/5"
                      }`;
                      const iconClass = item.premium
                        ? "text-[#3F2D1B] flex-shrink-0 mt-0.5"
                        : (item.accent === "rose" || item.accent === "green")
                        ? "text-white flex-shrink-0 mt-0.5 drop-shadow"
                        : item.highlight
                        ? "text-gold flex-shrink-0 mt-0.5"
                        : "text-[#3F2D1B] flex-shrink-0 mt-0.5";
                      const labelClass = `text-sm font-medium ${
                        (item.accent === "rose" || item.accent === "green") ? "text-white" : (item.premium || item.highlight) ? "text-[#3F2D1B]" : ""
                      }`;
                      const hintClass = (item.accent === "rose" || item.accent === "green")
                        ? "text-white/85 text-[11px] mt-0.5 leading-snug"
                        : item.premium
                        ? "text-[#3F2D1B]/70 text-[11px] mt-0.5 leading-snug"
                        : "text-muted text-[11px] mt-0.5 leading-snug";
                      const inner = (
                        <>
                          <Icon size={20} weight={(item.premium || item.accent === "rose" || item.accent === "green") ? "fill" : "duotone"} className={iconClass} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={labelClass}>{item.label}</span>
                              {item.isNew && (
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#3F2D1B] text-[#FFB800] font-semibold leading-none">
                                  NEW
                                </span>
                              )}
                              {item.badge > 0 && (
                                <span
                                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-600 text-white leading-none min-w-[18px] text-center"
                                  data-testid="menu-badge">
                                  {item.badge > 99 ? "99+" : item.badge}
                                </span>
                              )}
                            </div>
                            {item.hint && <div className={hintClass}>{item.hint}</div>}
                          </div>
                        </>
                      );
                      const testId = `mobile-menu-link-${section.id}-${i}`;
                      if (item.action === "open-astro-portal") {
                        return (
                          <button
                            key={`${item.label}-${i}`}
                            type="button"
                            onClick={async (e) => {
                              e?.preventDefault?.();
                              // Capture the current pathname BEFORE we
                              // close the menu — onClose triggers an
                              // unmount-animation that can race with
                              // navigation timing.
                              const onAstroPage = window.location.pathname.startsWith("/astrologer/");
                              onClose?.();
                              // If we're ALREADY on an astrologer page,
                              // a same-route navigate is a no-op in
                              // React Router → Priya saw "nothing
                              // happens" and had to refresh. Force-
                              // reload to /astrologer/dashboard so the
                              // page remounts cleanly.
                              if (onAstroPage && window.location.pathname === "/astrologer/dashboard") {
                                window.location.assign("/astrologer/dashboard");
                                return;
                              }
                              // SSO call with an explicit 4s timeout so
                              // a slow network NEVER leaves the button
                              // hanging silently — user always lands
                              // somewhere within 4s.
                              const ssoCall = api.post(
                                "/astrologer/sso-from-customer",
                                {},
                                { timeout: 4000 },
                              );
                              try {
                                await ssoCall;
                                navigate("/astrologer/dashboard");
                              } catch (err) {
                                // 404: this email isn't linked to an
                                // astrologer profile → manual login.
                                // 401/400/network: try the astro cookie
                                // directly — if it's still valid we can
                                // skip the login screen entirely.
                                const status = err?.response?.status;
                                if (status === 404) {
                                  navigate("/astrologer/login");
                                  return;
                                }
                                try {
                                  await api.get("/astrologer/me", { timeout: 3000 });
                                  navigate("/astrologer/dashboard");
                                } catch (_) {
                                  navigate("/astrologer/login");
                                }
                              }
                            }}
                            className={containerClass}
                            data-testid="mobile-menu-astro-portal-btn"
                          >
                            {inner}
                          </button>
                        );
                      }
                      if (item.action === "open-vip-offer") {
                        return (
                          <button
                            key={`${item.label}-${i}`}
                            type="button"
                            onClick={() => {
                              onClose?.();
                              setTimeout(() => onOpenVipOffer?.(), 80);
                            }}
                            className={containerClass}
                            data-testid="mobile-menu-vip-king-btn"
                          >
                            {inner}
                          </button>
                        );
                      }
                      if (item.external) {
                        return (
                          <a
                            key={`${item.label}-${i}`}
                            href={item.to}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => onClose?.()}
                            className={containerClass}
                            data-testid={testId}
                          >
                            {inner}
                          </a>
                        );
                      }
                      return (
                        <Link
                          key={`${item.label}-${i}`}
                          to={item.to}
                          onClick={() => {
                            onClose?.();
                            if (item.event) {
                              setTimeout(() => window.dispatchEvent(new CustomEvent(item.event)), 50);
                            }
                          }}
                          className={containerClass}
                          data-testid={testId}
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </nav>

        {/* Footer actions */}
        <div className="border-t border-soft px-3 py-3 space-y-1" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}>
          {user && (
            <button
              onClick={() => { onClose?.(); onLogout?.(); }}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-md hover:bg-black/5 text-sm text-rose-700"
              data-testid="mobile-menu-logout-btn"
            >
              <SignOut size={18} />
              Sign out
            </button>
          )}
          <p className="text-muted text-[11px] text-center px-2 pt-1 font-light">
            © 2026 AstroKuberChat · Crafted under cosmic light
          </p>
          <p className="text-muted text-[10px] text-center px-2 font-light">
            An innovative astrology platform by MessengerofShiva
          </p>
        </div>
      </aside>
    </>,
    document.body,
  );
}

export const Footer = () => (
  <>
  <ToolReportPromo />
  <footer className="border-t border-soft mt-32" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div>
        <div className="font-serif-display text-2xl">Astro<span className="text-gold">Kuber</span>Chat</div>
        <p className="text-muted text-sm mt-2 max-w-md">Guidance written in the stars, available whenever you need it.</p>
        <p className="text-muted text-xs mt-3 max-w-md" data-testid="footer-brand-line">
          AstroKuberChat — an innovative astrology platform by <span className="font-medium text-[#3F2D1B]">MessengerofShiva</span>.
        </p>
      </div>
      <div className="flex flex-col items-start md:items-end gap-3 w-full md:w-auto">
        <Link
          to="/contact"
          className="text-sm text-[#3F2D1B] hover:text-gold transition-colors inline-flex items-center gap-1.5 font-medium"
          data-testid="footer-contact-us"
        >
          <Envelope size={14} weight="duotone" /> Have a query? Contact us
        </Link>
        <Link
          to="/astrologer/register"
          className="btn-ghost rounded-full px-5 py-2.5 text-sm font-medium"
          data-testid="footer-become-astrologer"
        >
          Become an astrologer →
        </Link>
        {/* Mobile-safe link list — wraps to multiple lines so no link is
            clipped by the viewport edge (Shipping & Exchange, Delete Account,
            etc. were being cut on iPhone). */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:justify-end">
          <Link to="/store" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-store">
            Store
          </Link>
          <span className="text-muted">&middot;</span>
          <Link to="/blog" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-blog">
            Blog
          </Link>
          <span className="text-muted">&middot;</span>
          <Link to="/privacy" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-privacy">
            Privacy Policy
          </Link>
          <span className="text-muted">&middot;</span>
          <Link to="/terms" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-terms">
            Terms of Service
          </Link>
          <span className="text-muted">&middot;</span>
          <Link to="/refund" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-refund">
            Cancellation &amp; Refund
          </Link>
          <span className="text-muted">&middot;</span>
          <Link to="/shipping" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-shipping">
            Shipping &amp; Exchange
          </Link>
          <span className="text-muted">&middot;</span>
          <Link to="/contact" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-contact">
            Contact Us
          </Link>
          {/* Required by Google Play Data Safety — externally reachable
              link where users can request account + data deletion without
              installing the app. */}
          <span className="text-muted">&middot;</span>
          <Link to="/account-deletion" className="text-muted hover:text-[#3F2D1B] transition-colors" data-testid="footer-account-deletion">
            Delete Account
          </Link>
        </div>
        <div className="label-eyebrow text-muted whitespace-normal break-words">© 2026 AstroKuberChat · Crafted under cosmic light</div>
      </div>
    </div>
  </footer>
  </>
);

export const PageShell = ({ children, hideHeader = false }) => (
  // On mobile the fixed <MobileBottomTabBar /> floats over the last ~100px of
  // the viewport. Without this bottom pad, the site Footer (Terms, Privacy,
  // Contact, Store, Blog, Refund, etc.) is permanently obscured on iOS &
  // Android. `sm:pb-0` restores the desktop default (no tab bar there).
  <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+96px)] sm:pb-0">
    {!hideHeader && <Header />}
    <main>{children}</main>
    <Footer />
  </div>
);
